import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';

import type { MediaLibraryBrandFacet } from '../media.api';

/** Pictogramnamen en weergavewaarden — geen tekst voor de gebruiker. */
const GRID_ICON = 'grid_view';
const LIST_ICON = 'view_list';
const GRID_VIEW = 'grid';
const LIST_VIEW = 'list';


export interface MediaFiltersProps {
  query: string;
  onQueryChange: (value: string) => void;
  brandId: string | null;
  onBrandChange: (value: string | null) => void;
  brands: MediaLibraryBrandFacet[];
  /** "Niet gekoppeld" facet (h11-4, FR52): null = no filter, false = only unlinked files. */
  linked: boolean | null;
  onLinkedChange: (value: boolean | null) => void;
  /** Hoeveel bestanden er op een pagina staan — een weergavekeuze, geen filter. */
  pageSize: number;
  pageSizeChoices: readonly number[];
  onPageSizeChange: (value: number) => void;
  view: 'grid' | 'list';
  onViewChange: (value: 'grid' | 'list') => void;
  /**
   * Bij een storing rendert het raster niet. De keuze hoe je kijkt en hoeveel je ziet hoort dan
   * niet los boven een lege plek te staan; de filters blijven wél, want daarmee kom je eruit.
   */
  showViewControls?: boolean;
}

/**
 * Paginafilters van de mediabibliotheek (zoeken, merk, "niet gekoppeld") plus rijen per pagina en
 * weergave. Type, sortering en reset staan sinds spec-tabelfilters story 10 in de centrale
 * filterbalk; MediaLibraryPage houdt alles in de lijststand. Puur presentatie.
 */
export function MediaFilters({
  query, onQueryChange, brandId, onBrandChange, brands, linked, onLinkedChange,
  pageSize, pageSizeChoices, onPageSizeChange, view, onViewChange,
  showViewControls = true,
}: MediaFiltersProps) {
  const { t } = useTranslation();
  const unlinkedOnly = linked === false;

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="media-filters">
      <input
        aria-label={t('media.library.filters.searchLabel')}
        className="h-9 max-w-[320px] flex-1 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={t('media.library.filters.searchPlaceholder')}
        type="search"
        value={query}
      />

      <select
        aria-label={t('media.library.filters.brandLabel')}
        className="h-9 max-w-[220px] rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        onChange={(event) => onBrandChange(event.target.value === '' ? null : event.target.value)}
        value={brandId ?? ''}
      >
        <option value="">{t('media.library.filters.allBrands')}</option>
        {brands.map((brand) => (
          <option key={brand.brandId} value={brand.brandId}>{t('media.library.filters.brandOption', { name: brand.name, count: brand.count })}</option>
        ))}
      </select>

      <Button
        aria-pressed={unlinkedOnly}
        onClick={() => onLinkedChange(unlinkedOnly ? null : false)}
        size="sm"
        variant={unlinkedOnly ? 'secondary' : 'outline'}
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[16px]">link_off</span>
        {t('media.library.filters.unlinkedOnly')}
      </Button>

      {showViewControls && (
      <div className="ml-auto flex items-center gap-2">
        <select
          aria-label={t('media.library.filters.pageSizeLabel')}
          className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          value={pageSize}
        >
          {pageSizeChoices.map((choice) => (
            <option key={choice} value={choice}>{t('media.library.filters.pageSizeOption', { count: choice })}</option>
          ))}
        </select>

        <div aria-label={t('media.library.filters.viewLabel')} className="flex items-center gap-1" role="group">
          <Button
            aria-label={t('media.library.filters.viewGrid')}
            aria-pressed={view === GRID_VIEW}
            onClick={() => onViewChange(GRID_VIEW)}
            size="sm"
            variant={view === GRID_VIEW ? 'default' : 'outline'}
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">{GRID_ICON}</span>
          </Button>
          <Button
            aria-label={t('media.library.filters.viewList')}
            aria-pressed={view === LIST_VIEW}
            onClick={() => onViewChange(LIST_VIEW)}
            size="sm"
            variant={view === LIST_VIEW ? 'default' : 'outline'}
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">{LIST_ICON}</span>
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}
